import { Link } from 'react-router-dom';
import { ArrowUpRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { PORTFOLIO } from '../data/content';

export default function PortfolioGrid({ limit }) {
  const { t } = useTranslation();
  const items = typeof limit === 'number' ? PORTFOLIO.slice(0, limit) : PORTFOLIO;

  return (
    <div
      data-testid="portfolio-grid"
      className="flex flex-wrap justify-center gap-[1px] bg-[#0B1F3F]/8 border border-[#0B1F3F]/10"
    >
      {items.map((c) => {
        const name = t(`portfolio.${c.slug}.name`);
        const sector = t(`portfolio.${c.slug}.sector`);
        const description = t(`portfolio.${c.slug}.description`);
        const location = t(`portfolio.${c.slug}.location`, { defaultValue: c.location });
        const country = t(`portfolio.${c.slug}.country`, { defaultValue: c.country });
        const tagline = t(`portfolio.${c.slug}.tagline`, { defaultValue: '' });

        return (
          <article
            key={c.slug}
            data-testid={`portfolio-card-${c.slug}`}
            className="bg-[#EBE3D0] group hover:bg-[#8B6F47] transition-colors duration-500 relative overflow-hidden flex flex-col w-full md:w-[calc(50%-1px)] lg:w-[calc(33.333%-1px)]"
          >
            <div
              data-testid={`portfolio-logo-${c.slug}`}
              className="relative aspect-[5/3] flex items-center justify-center px-6 py-6 md:px-8 md:py-8 border-b border-[#0B1F3F]/10 overflow-hidden bg-[#8B6F47]"
            >
              {c.logo ? (
                <img
                  src={c.logo}
                  alt={`${name} logo`}
                  loading="lazy"
                  className="max-w-[100%] max-h-[100%] object-contain opacity-95 group-hover:opacity-100 transition-all duration-[900ms] group-hover:scale-[1.04]"
                />
              ) : (
                <div className="text-center opacity-90">
                  <p className="font-serif text-3xl md:text-[34px] text-[#0B1F3F] leading-tight tracking-wide">
                    {c.shortName}
                  </p>
                  <p className="mt-3 text-[9px] tracking-[0.35em] uppercase text-[#6B5B4A]">
                    {country}
                  </p>
                </div>
              )}
              <div aria-hidden="true" className="absolute inset-0 pointer-events-none"
                   style={{ background: 'radial-gradient(ellipse at center, rgba(120,34,33,0.10) 0%, transparent 60%)' }}
              />
            </div>

            <div className="p-8 md:p-10 flex flex-col flex-grow">
              <div className="flex items-center justify-between mb-6 gap-3">
                <span className="text-[10px] font-sans tracking-[0.25em] text-[#B58F45] uppercase">
                  {sector}
                </span>
                <span className="text-[10px] font-sans tracking-widest text-[#6B5B4A] uppercase text-right">
                  {country}
                </span>
              </div>
              <h3 className="font-serif text-2xl md:text-[26px] text-[#0B1F3F] mb-2 leading-tight">
                {name}
              </h3>
              {tagline && (
                <p className="font-serif italic text-sm text-[#B58F45] mb-4">
                  &ldquo;{tagline}&rdquo;
                </p>
              )}
              <p className="text-[#6B5B4A] font-sans text-[14px] leading-relaxed flex-grow mb-6 mt-2">
                {description}
              </p>

              {c.contact && (
                <div className="border-t border-[#0B1F3F]/10 pt-5 mb-6 space-y-2" data-testid={`portfolio-contact-${c.slug}`}>
                  {c.contact.address && (
                    <p className="text-[11px] font-sans text-[#6B5B4A] leading-snug">
                      {c.contact.address}
                    </p>
                  )}
                  <div className="flex flex-col gap-1 pt-1">
                    {c.contact.email && (
                      <a
                        href={`mailto:${c.contact.email}`}
                        className="text-[12px] font-sans text-[#0B1F3F] hover:text-[#B58F45] transition-colors break-all"
                        data-testid={`portfolio-email-${c.slug}`}
                      >
                        {c.contact.email}
                      </a>
                    )}
                    {c.contact.phone && (
                      <a
                        href={`tel:${c.contact.phone.replace(/\s+/g, '')}`}
                        className="text-[12px] font-sans text-[#6B5B4A] hover:text-[#B58F45] transition-colors"
                        data-testid={`portfolio-phone-${c.slug}`}
                      >
                        T · {c.contact.phone}
                      </a>
                    )}
                    {c.contact.mobile && (
                      <a
                        href={`tel:${c.contact.mobile.replace(/\s+/g, '')}`}
                        className="text-[12px] font-sans text-[#6B5B4A] hover:text-[#B58F45] transition-colors"
                        data-testid={`portfolio-mobile-${c.slug}`}
                      >
                        M · {c.contact.mobile}
                      </a>
                    )}
                    {c.contact.website && (
                      <a
                        href={c.contact.website}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-[12px] font-sans text-[#0B1F3F] hover:text-[#B58F45] transition-colors break-all"
                        data-testid={`portfolio-website-${c.slug}`}
                      >
                        {c.contact.website.replace(/^https?:\/\//, '')}
                      </a>
                    )}
                  </div>
                </div>
              )}

              <Link
                to="/contact"
                data-testid={`portfolio-link-${c.slug}`}
                className="text-xs font-sans tracking-[0.25em] uppercase text-[#0B1F3F] group-hover:text-[#B58F45] transition-colors duration-300 flex items-center gap-2 mt-auto"
              >
                {t('portfolio.enquire_about', { name: c.shortName })}
                <ArrowUpRight size={14} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
            </div>
          </article>
        );
      })}
    </div>
  );
}
